import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Date formatting utility - Convert various formats to DD-MM-YYYY
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  try {
    // Handle Date objects
    if (dateStr instanceof Date) {
      if (isNaN(dateStr.getTime())) return 'N/A';
      const day = String(dateStr.getDate()).padStart(2, '0');
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const year = dateStr.getFullYear();
      return `${day}-${month}-${year}`;
    }
    
    // Handle ISO datetime format (YYYY-MM-DDTHH:MM:SS)
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }
    
    // Handle YYYY-MM-DD format
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    }
    
    // Already in DD-MM-YYYY format
    if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Try to parse as date
    const parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const year = parsedDate.getFullYear();
      return `${day}-${month}-${year}`;
    }
    
    return 'N/A';
  } catch (e) {
    console.error('Date formatting error:', e);
    return 'N/A';
  }
};

// Format datetime to DD-MM-YYYY HH:MM
const formatDateTimeDisplay = (dateTime, time = null) => {
  if (!dateTime) return 'N/A';
  
  try {
    // If time is provided separately
    if (time) {
      const formattedDate = formatDateDisplay(dateTime);
      return formattedDate !== 'N/A' ? `${formattedDate} ${time}` : 'N/A';
    }
    
    // Handle ISO datetime format
    if (typeof dateTime === 'string' && dateTime.includes('T')) {
      const [datePart, timePart] = dateTime.split('T');
      const formattedDate = formatDateDisplay(datePart);
      if (timePart && formattedDate !== 'N/A') {
        const timeOnly = timePart.split('.')[0].substring(0, 5); // HH:MM
        return `${formattedDate} ${timeOnly}`;
      }
      return formattedDate;
    }
    
    // Handle Date objects
    if (dateTime instanceof Date && !isNaN(dateTime.getTime())) {
      const formattedDate = formatDateDisplay(dateTime);
      const hours = String(dateTime.getHours()).padStart(2, '0');
      const minutes = String(dateTime.getMinutes()).padStart(2, '0');
      return `${formattedDate} ${hours}:${minutes}`;
    }
    
    return formatDateDisplay(dateTime);
  } catch (e) {
    console.error('DateTime formatting error:', e);
    return 'N/A';
  }
};

// Get current timestamp in DD-MM-YYYY HH:MM format
const getCurrentTimestamp = () => {
  const now = new Date();
  const date = formatDateDisplay(now);
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${date} ${hours}:${minutes}`;
};

// Load and convert logo to base64
const loadLogoAsBase64 = async () => {
  try {
    const response = await fetch('/customer-assets/Ezy_Mobility_Logo_New.png');
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

// Export to Excel with Ezy Mobility Branding
export const exportToExcel = async (data, filename, sheetName = 'Sheet1', stats = null) => {
  // Add branded header rows with company info
  const headerRows = [
    [''],
    ['EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC'],
    [''],
    ['═══════════════════════════════════════════════════════════════════════════════════'],
    [`Report: ${sheetName}`],
    [`Generated: ${getCurrentTimestamp()} (UAE Time)`],
    ['═══════════════════════════════════════════════════════════════════════════════════'],
    ['']
  ];

  // Add quick stats summary if provided
  if (stats && stats.length > 0) {
    headerRows.push(['📊 QUICK STATS SUMMARY']);
    headerRows.push(['─────────────────────────────────────────────────────────────────']);
    stats.forEach(stat => {
      headerRows.push([`${stat.label}: ${stat.value}`]);
    });
    headerRows.push(['─────────────────────────────────────────────────────────────────']);
    headerRows.push(['']);
  }

  headerRows.push([]); // Empty row before data
  
  // Combine header and data
  const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
  XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A10', skipHeader: false });
  
  // Calculate optimal column widths based on content
  const colCount = Object.keys(data[0] || {}).length;
  const colWidths = [];
  
  // Calculate width for each column
  for (let i = 0; i < colCount; i++) {
    let maxWidth = 15; // minimum width
    const colLetter = XLSX.utils.encode_col(i);
    
    // Check header width
    const headerCell = worksheet[`${colLetter}10`];
    if (headerCell && headerCell.v) {
      maxWidth = Math.max(maxWidth, headerCell.v.toString().length + 2);
    }
    
    // Check data widths (sample first 50 rows for performance)
    for (let j = 0; j < Math.min(50, data.length); j++) {
      const cellRef = `${colLetter}${j + 11}`;
      const cell = worksheet[cellRef];
      if (cell && cell.v) {
        maxWidth = Math.max(maxWidth, cell.v.toString().length + 2);
      }
    }
    
    colWidths.push({ wch: Math.min(maxWidth, 50) }); // max 50 chars width
  }
  
  // Set the first column (for headers) to be wider
  colWidths[0] = { wch: Math.max(colWidths[0]?.wch || 15, 85) };
  
  worksheet['!cols'] = colWidths;
  
  // Merge cells for company name header (row 2)
  worksheet['!merges'] = [
    { s: { r: 1, c: 0 }, e: { r: 1, c: Math.max(colCount - 1, 5) } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: Math.max(colCount - 1, 5) } }
  ];
  
  // Create workbook and append sheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export to PDF with Ezy Mobility Branding - Modern Dashboard Style
export const exportToPDF = async (columns, data, filename, title, stats = null) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
  
  // Load logo
  const logoBase64 = await loadLogoAsBase64();
  
  // HEADER SECTION - Gradient background
  doc.setFillColor(139, 195, 74); // Lime green
  doc.rect(0, 0, 297, 35, 'F');
  
  // Add gradient effect with lighter overlay
  doc.setFillColor(174, 213, 129); // Lighter lime
  doc.setGState(new doc.GState({opacity: 0.3}));
  doc.rect(0, 0, 297, 35, 'F');
  doc.setGState(new doc.GState({opacity: 1}));
  
  // Logo with white background box
  if (logoBase64) {
    try {
      // White rounded box for logo
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(14, 8, 30, 18, 2, 2, 'F');
      // Add logo
      doc.addImage(logoBase64, 'PNG', 17, 10, 24, 14);
    } catch (error) {
      console.error('Failed to add logo to PDF:', error);
    }
  }
  
  // Company name in white
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC', 48, 15);
  
  // Report title in white
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(title, 48, 25);
  
  // Generated date in white
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.text(`Generated: ${getCurrentTimestamp()} (UAE Time)`, 48, 30);
  
  let currentY = 43;
  
  // STATS SECTION - Modern card grid
  if (stats && stats.length > 0) {
    // Stats section background
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(14, currentY, 269, 48, 3, 3, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.08}));
    doc.setLineWidth(0.1);
    doc.roundedRect(14, currentY, 269, 48, 3, 3, 'S');
    doc.setGState(new doc.GState({opacity: 1}));
    
    currentY += 7;
    
    // Stats title
    doc.setFontSize(10);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('QUICK STATS SUMMARY', 20, currentY);
    
    currentY += 7;
    
    // Stats cards in grid (4 per row)
    const statsPerRow = 4;
    const cardWidth = 63;
    const cardHeight = 14;
    const cardGap = 4;
    const startX = 20;
    
    for (let i = 0; i < stats.length; i++) {
      const stat = stats[i];
      const row = Math.floor(i / statsPerRow);
      const col = i % statsPerRow;
      const cardX = startX + (col * (cardWidth + cardGap));
      const cardY = currentY + (row * (cardHeight + 3));
      
      // Card background with gradient effect
      doc.setFillColor(248, 250, 247); // Very light lime
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, 'F');
      
      // Left accent border
      doc.setFillColor(139, 195, 74);
      doc.roundedRect(cardX, cardY, 2, cardHeight, 1, 1, 'F');
      
      // Stat label
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'bold');
      doc.text(stat.label.toUpperCase(), cardX + 5, cardY + 5);
      
      // Stat value
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.setFont(undefined, 'bold');
      doc.text(String(stat.value), cardX + 5, cardY + 11);
    }
    
    // Calculate current Y based on rows
    const totalRows = Math.ceil(stats.length / statsPerRow);
    currentY += (totalRows * (cardHeight + 3)) + 5;
  }
  
  // TABLE SECTION
  currentY += 5;
  
  autoTable(doc, {
    startY: currentY,
    head: [columns],
    body: data,
    theme: 'grid',
    styles: { 
      fontSize: 8,
      cellPadding: 3,
      lineColor: [232, 232, 232],
      lineWidth: 0.1
    },
    headStyles: { 
      fillColor: [139, 195, 74], // Lime green
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left'
    },
    alternateRowStyles: { 
      fillColor: [250, 250, 250]
    },
    margin: { left: 14, right: 14 },
    tableWidth: 269
  });
  
  // FOOTER on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    const footerY = doc.internal.pageSize.height - 15;
    
    // Footer accent line
    doc.setDrawColor(139, 195, 74);
    doc.setLineWidth(1.5);
    doc.line(14, footerY, 283, footerY);
    
    // Footer text
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.setFont(undefined, 'normal');
    doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC - Fleet Management System', 14, footerY + 5);
    
    // Page number
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, footerY + 5);
  }
  
  doc.save(`${filename}.pdf`);
};

// Bike Log Report
export const exportBikeLog = async (motorcycles, format = 'excel') => {
  const data = motorcycles.map(bike => ({
    'Plate Number': bike.plate_number,
    'Chassis Number': bike.chassis_number,
    'Model': bike.model_name,
    'Version': bike.model_version,
    'Color': bike.color,
    'Year': bike.year_of_manufacturing,
    'Status': bike.status,
    'Lease Status': bike.lease_status,
    'Lessee': bike.lessee_name || 'N/A',
    'Insurance Provider': bike.insurance_provider || 'N/A',
    'Insurance Expiry': bike.insurance_expiry_date || 'N/A'
  }));

  // Calculate stats
  const stats = [
    { label: 'Total Motorcycles', value: motorcycles.length },
    { label: 'Active', value: motorcycles.filter(b => b.status === 'active').length },
    { label: 'In Maintenance', value: motorcycles.filter(b => b.status === 'maintenance').length },
    { label: 'Inactive', value: motorcycles.filter(b => b.status === 'inactive').length },
    { label: 'Leased', value: motorcycles.filter(b => b.lease_status === 'leased').length },
    { label: 'Available', value: motorcycles.filter(b => b.lease_status === 'available').length }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Bike_Log_Report', 'Bike Log', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Bike_Log_Report', 'Motorcycle Fleet - Bike Log Report', stats);
  }
};

// Maintenance Log Report
export const exportMaintenanceLog = async (maintenanceRecords, format = 'excel') => {
  const data = maintenanceRecords.map(record => ({
    'Date': record.maintenance_date,
    'Time': record.maintenance_time,
    'Bike Plate': record.bike_plate_number,
    'Rider': record.rider_name || 'N/A',
    'Type': record.type,
    'Technician': record.technician,
    'Repair Items': record.repair_items?.length || 0,
    'Total Cost (Dh)': record.total_cost.toFixed(2),
    'Status': record.status,
    'Notes': record.notes || 'N/A'
  }));

  // Calculate stats
  const totalCost = maintenanceRecords.reduce((sum, r) => sum + (r.total_cost || 0), 0);
  const avgCost = maintenanceRecords.length > 0 ? totalCost / maintenanceRecords.length : 0;
  const stats = [
    { label: 'Total Records', value: maintenanceRecords.length },
    { label: 'Total Cost', value: `Dh ${totalCost.toFixed(2)}` },
    { label: 'Average Cost', value: `Dh ${avgCost.toFixed(2)}` },
    { label: 'Completed', value: maintenanceRecords.filter(r => r.status === 'completed').length },
    { label: 'Pending', value: maintenanceRecords.filter(r => r.status === 'pending').length },
    { label: 'Total Repair Items', value: maintenanceRecords.reduce((sum, r) => sum + (r.repair_items?.length || 0), 0) }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Maintenance_Log_Report', 'Maintenance Log', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Maintenance_Log_Report', 'Maintenance Log Report', stats);
  }
};

// Bikes Assigned to Lessee Report
export const exportBikesAssignedToLessee = async (motorcycles, format = 'excel') => {
  const leasedBikes = motorcycles.filter(bike => bike.lease_status === 'leased');
  
  const data = leasedBikes.map(bike => ({
    'Lessee Company': bike.lessee_name,
    'Plate Number': bike.plate_number,
    'Model': `${bike.model_name} ${bike.model_version}`,
    'Color': bike.color,
    'Lease Start Date': bike.lease_start_date || 'N/A',
    'Monthly Amount (Dh)': bike.monthly_lease_amount || 0,
    'Lessee Contact': bike.lessee_contact || 'N/A'
  }));

  // Calculate stats
  const totalMonthlyRevenue = leasedBikes.reduce((sum, bike) => sum + (bike.monthly_lease_amount || 0), 0);
  const uniqueLessees = [...new Set(leasedBikes.map(bike => bike.lessee_name))].filter(Boolean);
  const avgLeaseAmount = leasedBikes.length > 0 ? totalMonthlyRevenue / leasedBikes.length : 0;
  
  const stats = [
    { label: 'Total Leased Bikes', value: leasedBikes.length },
    { label: 'Unique Lessees', value: uniqueLessees.length },
    { label: 'Total Monthly Revenue', value: `Dh ${totalMonthlyRevenue.toFixed(2)}` },
    { label: 'Average Lease Amount', value: `Dh ${avgLeaseAmount.toFixed(2)}` }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Bikes_Assigned_to_Lessee_Report', 'Leased Bikes', stats);
  } else {
    const columns = Object.keys(data[0] || {});
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Bikes_Assigned_to_Lessee_Report', 'Bikes Assigned to Lessee Report', stats);
  }
};

// Bikes with Riders Report
export const exportBikesWithRiders = async (motorcycles, riders, handovers = [], format = 'excel') => {
  const data = motorcycles.map(bike => {
    // Find assigned rider - check both assigned_bike_id and using rider_id/assigned_rider_id from bike
    const assignedRider = riders.find(r => r.assigned_bike_id === bike.id) || 
                          riders.find(r => r.id === (bike.rider_id || bike.assigned_rider_id));
    
    // Find most recent handover for this bike
    const bikeHandovers = handovers.filter(h => h.bike_id === bike.id || h.bike_plate_number === bike.plate_number);
    const latestHandover = bikeHandovers.sort((a, b) => 
      new Date(b.handover_date) - new Date(a.handover_date)
    )[0];
    
    return {
      'Plate Number': bike.plate_number,
      'Model': `${bike.model_name} ${bike.model_version}`,
      'Assigned Rider': assignedRider?.name || bike.rider_name || bike.assigned_rider_name || 'Not Assigned',
      'Handover Date': latestHandover?.handover_date || 'N/A',
      'Rider License': assignedRider?.license_number || 'N/A',
      'Rider Contact': assignedRider?.mobile_uae || 'N/A',
      'Platform': assignedRider?.platform || 'N/A',
      'Status': bike.status
    };
  });

  // Calculate stats
  const assignedBikes = motorcycles.filter(bike => {
    const assignedRider = riders.find(r => r.assigned_bike_id === bike.id) || 
                          riders.find(r => r.id === (bike.rider_id || bike.assigned_rider_id));
    return assignedRider || bike.rider_name || bike.assigned_rider_name;
  });
  const unassignedBikes = motorcycles.length - assignedBikes.length;
  const bikesWithHandovers = motorcycles.filter(bike => 
    handovers.some(h => h.bike_id === bike.id || h.bike_plate_number === bike.plate_number)
  );
  
  const stats = [
    { label: 'Total Motorcycles', value: motorcycles.length },
    { label: 'Assigned to Riders', value: assignedBikes.length },
    { label: 'Unassigned', value: unassignedBikes },
    { label: 'Total Riders', value: riders.length },
    { label: 'With Handover Records', value: bikesWithHandovers.length },
    { label: 'Total Handovers', value: handovers.length }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Bikes_with_Riders_Report', 'Bikes & Riders', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Bikes_with_Riders_Report', 'Bikes with Riders Assignment Report', stats);
  }
};

// Monthly Repair and Maintenance Summary
export const exportMonthlyMaintenanceSummary = async (motorcycles, maintenanceRecords, month, year, format = 'excel') => {
  const monthStr = month.toString().padStart(2, '0');

  // Filter maintenance records for the selected month
  const monthlyRecords = maintenanceRecords.filter(record => {
    const d = record.maintenance_date || '';
    return d.startsWith(`${year}-${monthStr}`);
  });

  // Count distinct maintenance JOBS per bike for the period (not items)
  const bikeMaintCount = {};
  monthlyRecords.forEach(record => {
    const plate = record.bike_plate_number || record.motorcycle_plate || 'N/A';
    bikeMaintCount[plate] = (bikeMaintCount[plate] || 0) + 1;
  });

  // Build ONE ROW PER REPAIR ITEM EXECUTED (full cost breakdown).
  // Each maintenance with N items expands into N rows so the user can
  // pivot/sum/filter by item across the month.
  const data = [];
  monthlyRecords.forEach(record => {
    const plate = record.bike_plate_number || record.motorcycle_plate || 'N/A';
    const baseRow = {
      'Bike No.': plate,
      'Lessee Name': record.lessee_name || 'N/A',
      'Rider Name': record.rider_name || 'N/A',
      'Date of Maintenance': record.maintenance_date || 'N/A',
      'Time': record.maintenance_time || '',
      'Job Type': record.type || '',
      'Status': record.status || '',
      'Payment Status': record.payment_status || '',
      'Technician': record.technician || '',
      'Maintenance Count (Bike)': bikeMaintCount[plate] || 1,
    };

    const items = record.repair_items || [];
    if (items.length === 0) {
      // No itemised breakdown — fall back to the maintenance-level total
      data.push({
        ...baseRow,
        'Repair Item': 'No itemised breakdown',
        'Quantity': '',
        'Unit Rate (Dh)': '',
        'Line Total (Dh)': Number(record.total_cost || 0).toFixed(2),
        'Maintenance Total (Dh)': Number(record.total_cost || 0).toFixed(2),
        'Cost Remarks': record.cost_remarks || '',
      });
    } else {
      items.forEach((item, idx) => {
        const name = item.item_name || item.repair_item_name || 'Unnamed';
        const qty = Number(item.quantity || 1);
        const rate = Number(item.rate || 0);
        const lineTotal = +(qty * rate).toFixed(2);
        data.push({
          ...baseRow,
          'Repair Item': qty > 1 ? `${name} x${qty}` : name,
          'Quantity': qty,
          'Unit Rate (Dh)': rate.toFixed(2),
          'Line Total (Dh)': lineTotal.toFixed(2),
          // Show maintenance total only on the FIRST item row of each job —
          // keeps the column meaningful for pivot tables.
          'Maintenance Total (Dh)': idx === 0 ? Number(record.total_cost || 0).toFixed(2) : '',
          'Cost Remarks': idx === 0 ? (record.cost_remarks || '') : '',
        });
      });
    }
  });

  // If no records found, create an empty row
  if (data.length === 0) {
    data.push({
      'Bike No.': 'No records',
      'Lessee Name': '',
      'Rider Name': '',
      'Date of Maintenance': '',
      'Time': '',
      'Job Type': '',
      'Status': '',
      'Payment Status': '',
      'Technician': '',
      'Maintenance Count (Bike)': 0,
      'Repair Item': '',
      'Quantity': '',
      'Unit Rate (Dh)': '',
      'Line Total (Dh)': '0.00',
      'Maintenance Total (Dh)': '0.00',
      'Cost Remarks': '',
    });
  }

  // Calculate stats
  const totalMonthlyCost = monthlyRecords.reduce((sum, r) => sum + Number(r.total_cost || 0), 0);
  const uniqueBikes = [...new Set(monthlyRecords.map(r => r.bike_plate_number || r.motorcycle_plate))].filter(Boolean).length;
  const uniqueRiders = [...new Set(monthlyRecords.map(r => r.rider_name))].filter(Boolean).length;
  const uniqueLessees = [...new Set(monthlyRecords.map(r => r.lessee_name))].filter(Boolean).length;
  const allRepairItems = monthlyRecords.flatMap(r => (r.repair_items || []).map(i => i.item_name || i.repair_item_name)).filter(Boolean);
  const uniqueRepairTypes = [...new Set(allRepairItems)].length;
  const totalLineItems = allRepairItems.length;

  const stats = [
    { label: 'Reporting Period', value: `${monthStr}/${year}` },
    { label: 'Total Maintenance Jobs', value: monthlyRecords.length },
    { label: 'Total Line Items', value: totalLineItems },
    { label: 'Bikes Serviced', value: uniqueBikes },
    { label: 'Unique Riders', value: uniqueRiders },
    { label: 'Unique Lessees', value: uniqueLessees },
    { label: 'Total Cost', value: `Dh ${totalMonthlyCost.toFixed(2)}` },
    { label: 'Unique Repair Types', value: uniqueRepairTypes }
  ];

  if (format === 'excel') {
    exportToExcel(data, `Monthly_Maintenance_Summary_${year}_${monthStr}`, 'Monthly Summary', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(
      columns, 
      rows, 
      `Monthly_Maintenance_Summary_${year}_${monthStr}`, 
      `Monthly Repair & Maintenance Summary - ${monthStr}/${year}`,
      stats
    );
  }
};

// Fines Report
export const exportFinesReport = async (fines, format = 'excel') => {
  // Helper to format date as DD-MM-YYYY
  const formatDate = (fine) => {
    const raw = fine.date_of_fine || fine.fine_date || fine.fine_date_sortable || '';
    if (!raw || raw === 'N/A') return 'N/A';
    const s = String(raw).trim();
    // Already DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(s)) {
      const parts = s.split(/[-/]/);
      return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
    }
    // YYYY-MM-DD → convert to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return `${s.substring(8,10)}-${s.substring(5,7)}-${s.substring(0,4)}`;
    }
    return s;
  };

  const data = fines.map(fine => ({
    'Ticket No.': fine.ticket_number || fine.fine_number || 'N/A',
    'Date': formatDate(fine),
    'Time': fine.fine_time || '--:--',
    'Plate Number': fine.motorcycle_plate || fine.bike_plate_number || 'N/A',
    'Current Assigned Rider': fine.current_rider_name || 'N/A',
    'Fine by Rider': fine.fine_by_rider_name || fine.rider_name || 'N/A',
    'Lessee': fine.lessee_name || 'N/A',
    'Reason': fine.fine_type || fine.reason || 'N/A',
    'Fine Amount (Dh)': (fine.fine_amount || fine.amount || 0).toFixed(2),
    'Admin Fees (Dh)': (fine.admin_fee || fine.admin_fees || 0).toFixed(2),
    'Total (Dh)': ((fine.fine_amount || fine.amount || 0) + (fine.admin_fee || fine.admin_fees || 0)).toFixed(2),
    'Status': fine.status || 'N/A',
    'Location': fine.location || 'N/A'
  }));

  // Calculate stats
  const totalFineAmount = fines.reduce((sum, fine) => sum + (fine.fine_amount || fine.amount || 0), 0);
  const totalAdminFees = fines.reduce((sum, fine) => sum + (fine.admin_fee || fine.admin_fees || 0), 0);
  const totalAmount = totalFineAmount + totalAdminFees;
  const paidFines = fines.filter(fine => fine.status === 'paid').length;
  const pendingFines = fines.filter(fine => fine.status === 'pending').length;
  const uniqueRiders = [...new Set(fines.map(fine => fine.rider_name))].filter(Boolean).length;
  
  const stats = [
    { label: 'Total Fines', value: fines.length },
    { label: 'Paid', value: paidFines },
    { label: 'Pending', value: pendingFines },
    { label: 'Total Fine Amount', value: `Dh ${totalFineAmount.toFixed(2)}` },
    { label: 'Total Admin Fees', value: `Dh ${totalAdminFees.toFixed(2)}` },
    { label: 'Grand Total', value: `Dh ${totalAmount.toFixed(2)}` },
    { label: 'Unique Riders with Fines', value: uniqueRiders }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Fines_Report', 'Traffic Fines', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Fines_Report', 'Traffic Fines Report', stats);
  }
};


// Salik Report
export const exportSalikReport = async (salikRecords, format = 'excel') => {
  const data = salikRecords.map(salik => ({
    'Date': salik.salik_date || 'N/A',
    'Time': salik.salik_time || 'N/A',
    'Plate Number': salik.bike_plate_number || salik.motorcycle_plate || 'N/A',
    'Current Assigned Rider': salik.current_rider_name || 'N/A',
    'SALIK by Rider': salik.salik_by_rider_name || salik.rider_name || 'N/A',
    'Lessee': salik.lessee_name || 'N/A',
    'Gate Name': salik.salik_gate_name || salik.gate_name || 'N/A',
    'Amount (Dh)': Number(salik.amount || 0).toFixed(2),
    'Status': salik.status || 'N/A'
  }));

  // Calculate stats
  const totalSalikAmount = salikRecords.reduce((sum, salik) => sum + Number(salik.amount || 0), 0);
  const uniqueRiders = [...new Set(salikRecords.map(salik => salik.rider_name))].filter(Boolean).length;
  const uniqueGates = [...new Set(salikRecords.map(salik => salik.salik_gate_name || salik.gate_name))].filter(Boolean).length;
  const uniqueBikes = [...new Set(salikRecords.map(salik => salik.bike_plate_number || salik.motorcycle_plate))].filter(Boolean).length;
  
  const stats = [
    { label: 'Total Salik Records', value: salikRecords.length },
    { label: 'Total Salik Amount', value: `Dh ${totalSalikAmount.toFixed(2)}` },
    { label: 'Unique Bikes', value: uniqueBikes },
    { label: 'Unique Riders', value: uniqueRiders },
    { label: 'Unique Gates', value: uniqueGates }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Salik_Report', 'Salik Charges', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Salik_Report', 'Salik Charges Report', stats);
  }
};


// Lessees Report
export const exportLesseesReport = async (lessees, format = 'excel') => {
  const data = lessees.map(lessee => ({
    'Company Name': lessee.company_name,
    'Trade License': lessee.trade_license_no,
    'TRN': lessee.trn_no,
    'Owner Name': lessee.owner_name,
    'Owner Emirates ID': lessee.owner_emirates_id,
    'Contact Email': lessee.contact_email,
    'Contact Mobile': lessee.contact_mobile,
    'License Expiry': lessee.trade_license_expiry,
    'Status': lessee.status
  }));

  // Calculate stats
  const activeLessees = lessees.filter(lessee => lessee.status === 'active').length;
  const inactiveLessees = lessees.filter(lessee => lessee.status === 'inactive').length;
  
  // Check for expiring licenses (within 30 days)
  const today = new Date();
  const expiringLicenses = lessees.filter(lessee => {
    if (!lessee.trade_license_expiry) return false;
    const expiryDate = new Date(lessee.trade_license_expiry);
    const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  }).length;
  
  const stats = [
    { label: 'Total Lessee Companies', value: lessees.length },
    { label: 'Active', value: activeLessees },
    { label: 'Inactive', value: inactiveLessees },
    { label: 'Licenses Expiring Soon (30 days)', value: expiringLicenses }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Lessees_Report', 'Lessee Companies', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Lessees_Report', 'Lessee Companies Report', stats);
  }
};

// Riders Report
export const exportRidersReport = async (riders, format = 'excel') => {
  const data = riders.map(rider => ({
    'Name': rider.name,
    'Date of Birth': formatDateDisplay(rider.date_of_birth),
    'Emirates ID': rider.emirates_id,
    'Emirates ID Expiry': formatDateDisplay(rider.emirates_id_expiry),
    'Passport': rider.passport_number,
    'Passport Expiry': formatDateDisplay(rider.passport_expiry),
    'License': rider.license_number,
    'License Expiry': formatDateDisplay(rider.license_expiry),
    'UAE Mobile': rider.mobile_uae,
    'Platform': rider.platform,
    'Status': rider.status
  }));

  // Calculate stats
  const activeRiders = riders.filter(rider => rider.status === 'active').length;
  const inactiveRiders = riders.filter(rider => rider.status === 'inactive').length;
  
  // Check for expiring documents (within 30 days)
  const today = new Date();
  const expiringEmiratesIDs = riders.filter(rider => {
    if (!rider.emirates_id_expiry) return false;
    const expiryDate = new Date(rider.emirates_id_expiry);
    const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  }).length;
  
  const expiringLicenses = riders.filter(rider => {
    if (!rider.license_expiry) return false;
    const expiryDate = new Date(rider.license_expiry);
    const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  }).length;
  
  const platformBreakdown = riders.reduce((acc, rider) => {
    const platform = rider.platform || 'Not Specified';
    acc[platform] = (acc[platform] || 0) + 1;
    return acc;
  }, {});
  
  const stats = [
    { label: 'Total Riders', value: riders.length },
    { label: 'Active', value: activeRiders },
    { label: 'Inactive', value: inactiveRiders },
    { label: 'Emirates IDs Expiring Soon', value: expiringEmiratesIDs },
    { label: 'Licenses Expiring Soon', value: expiringLicenses },
    { label: 'Platform Distribution', value: Object.entries(platformBreakdown).map(([k, v]) => `${k}: ${v}`).join(', ') }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Riders_Report', 'Riders', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Riders_Report', 'Riders KYC Report', stats);
  }
};

// Batteries Report
export const exportBatteriesReport = async (batteries, format = 'excel') => {
  const data = batteries.map(battery => ({
    'Battery ID': battery.battery_id,
    'Serial Number': battery.serial_number,
    'Capacity (Ah)': battery.capacity_ah,
    'Health %': battery.health_percentage,
    'Status': battery.status,
    'Swap Count': battery.swap_count,
    'Last Swap Date': battery.last_swap_date || 'N/A'
  }));

  // Calculate stats
  const availableBatteries = batteries.filter(battery => battery.status === 'available').length;
  const inUseBatteries = batteries.filter(battery => battery.status === 'in-use').length;
  const chargingBatteries = batteries.filter(battery => battery.status === 'charging').length;
  const damagedBatteries = batteries.filter(battery => battery.status === 'damaged').length;
  
  const avgHealth = batteries.length > 0 
    ? (batteries.reduce((sum, b) => sum + (b.health_percentage || 0), 0) / batteries.length).toFixed(1)
    : 0;
  
  const totalSwaps = batteries.reduce((sum, b) => sum + (b.swap_count || 0), 0);
  const avgSwapsPerBattery = batteries.length > 0 ? (totalSwaps / batteries.length).toFixed(1) : 0;
  
  const lowHealthBatteries = batteries.filter(battery => battery.health_percentage < 70).length;
  
  const stats = [
    { label: 'Total Batteries', value: batteries.length },
    { label: 'Available', value: availableBatteries },
    { label: 'In Use', value: inUseBatteries },
    { label: 'Charging', value: chargingBatteries },
    { label: 'Damaged', value: damagedBatteries },
    { label: 'Average Health', value: `${avgHealth}%` },
    { label: 'Low Health (<70%)', value: lowHealthBatteries },
    { label: 'Total Swaps', value: totalSwaps },
    { label: 'Avg Swaps/Battery', value: avgSwapsPerBattery }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'Batteries_Report', 'Batteries', stats);
  } else {
    const columns = Object.keys(data[0]);
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'Batteries_Report', 'Battery Inventory Report', stats);
  }
};

// Activity Logs Report
export const exportActivityLogs = async (activities, format = 'excel') => {
  const data = activities.map(activity => ({
    'Date & Time': activity.date,
    'User': activity.user,
    'Module': activity.module,
    'Action': activity.action,
    'Entity ID': activity.entity_id,
    'Details': activity.details
  }));

  // Calculate stats
  const uniqueUsers = [...new Set(activities.map(activity => activity.user))].filter(Boolean).length;
  const moduleBreakdown = activities.reduce((acc, activity) => {
    const module = activity.module || 'Unknown';
    acc[module] = (acc[module] || 0) + 1;
    return acc;
  }, {});
  
  const actionBreakdown = activities.reduce((acc, activity) => {
    const action = activity.action || 'Unknown';
    acc[action] = (acc[action] || 0) + 1;
    return acc;
  }, {});
  
  const topModule = Object.entries(moduleBreakdown).sort((a, b) => b[1] - a[1])[0];
  const topAction = Object.entries(actionBreakdown).sort((a, b) => b[1] - a[1])[0];
  
  const stats = [
    { label: 'Total Activities', value: activities.length },
    { label: 'Unique Users', value: uniqueUsers },
    { label: 'Total Modules', value: Object.keys(moduleBreakdown).length },
    { label: 'Most Active Module', value: topModule ? `${topModule[0]} (${topModule[1]})` : 'N/A' },
    { label: 'Most Common Action', value: topAction ? `${topAction[0]} (${topAction[1]})` : 'N/A' }
  ];

  if (format === 'excel') {
    exportToExcel(data, 'User_Activity_Logs_Report', 'Activity Logs', stats);
  } else {
    const columns = Object.keys(data[0] || {});
    const rows = data.map(obj => Object.values(obj));
    await exportToPDF(columns, rows, 'User_Activity_Logs_Report', 'User Activity & Transaction Summary Report', stats);
  }
};



// Motorcycle History Report
export const exportMotorcycleHistory = async (historyData, format = 'pdf') => {
  const { motorcycle, summary, rider_assignments, idle_periods, maintenance_history, fines_history, salik_history, insurance_history, insurance_claims, accidents_history, battery_swaps_history } = historyData;
  
  if (format === 'pdf') {
    // For PDF, create comprehensive multi-section report
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Load logo
    const logoBase64 = await loadLogoAsBase64();
    
    // HEADER SECTION
    doc.setFillColor(139, 195, 74);
    doc.rect(0, 0, 297, 35, 'F');
    doc.setFillColor(174, 213, 129);
    doc.setGState(new doc.GState({opacity: 0.3}));
    doc.rect(0, 0, 297, 35, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    if (logoBase64) {
      try {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(14, 8, 30, 18, 2, 2, 'F');
        doc.addImage(logoBase64, 'PNG', 17, 10, 24, 14);
      } catch (error) {
        console.error('Failed to add logo:', error);
      }
    }
    
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC', 48, 15);
    
    doc.setFontSize(18);
    doc.text(`Motorcycle History Report - ${motorcycle.plate_number}`, 48, 25);
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${getCurrentTimestamp()} (UAE Time)`, 48, 30);
    
    let currentY = 43;
    
    // SUMMARY STATS SECTION
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(14, currentY, 269, 60, 3, 3, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.08}));
    doc.setLineWidth(0.1);
    doc.roundedRect(14, currentY, 269, 60, 3, 3, 'S');
    doc.setGState(new doc.GState({opacity: 1}));
    
    currentY += 7;
    doc.setFontSize(10);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('COMPREHENSIVE SUMMARY', 20, currentY);
    
    currentY += 7;
    
    const summaryStats = [
      { label: 'Total Riders Assigned', value: summary.total_riders_assigned },
      { label: 'Total Idle Days', value: summary.total_idle_days },
      { label: 'Maintenance Records', value: summary.total_maintenance_records },
      { label: 'Total Maintenance Cost', value: `Dh ${summary.total_maintenance_cost.toFixed(2)}` },
      { label: 'Total Fines', value: summary.total_fines },
      { label: 'Total Fines Amount', value: `Dh ${summary.total_fines_amount.toFixed(2)}` },
      { label: 'Total Salik Charges', value: summary.total_salik_charges },
      { label: 'Total Salik Amount', value: `Dh ${summary.total_salik_amount.toFixed(2)}` },
      { label: 'Insurance Renewals', value: summary.total_insurance_renewals },
      { label: 'Insurance Claims', value: summary.total_insurance_claims },
      { label: 'Claims Amount', value: `Dh ${summary.total_claims_amount.toFixed(2)}` },
      { label: 'Total Accidents', value: summary.total_accidents },
      { label: 'Battery Swaps', value: summary.total_battery_swaps || 0 }
    ];
    
    const statsPerRow = 4;
    const cardWidth = 63;
    const cardHeight = 14;
    const cardGap = 4;
    const startX = 20;
    
    for (let i = 0; i < summaryStats.length; i++) {
      const stat = summaryStats[i];
      const row = Math.floor(i / statsPerRow);
      const col = i % statsPerRow;
      const cardX = startX + (col * (cardWidth + cardGap));
      const cardY = currentY + (row * (cardHeight + 3));
      
      doc.setFillColor(248, 250, 247);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, 'F');
      doc.setFillColor(139, 195, 74);
      doc.roundedRect(cardX, cardY, 2, cardHeight, 1, 1, 'F');
      
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'bold');
      doc.text(stat.label.toUpperCase(), cardX + 5, cardY + 5);
      
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.setFont(undefined, 'bold');
      const valueStr = String(stat.value);
      doc.text(valueStr.length > 15 ? valueStr.substring(0, 12) + '...' : valueStr, cardX + 5, cardY + 11);
    }
    
    currentY += 65;
    
    // Add each section with tables
    const sections = [
      { title: 'RIDER ASSIGNMENT HISTORY', data: rider_assignments, columns: ['Rider Name', 'Start Date', 'End Date', 'Status', 'Duration (Days)'] },
      { title: 'IDLE PERIODS', data: idle_periods, columns: ['Start Date', 'End Date', 'Idle Days'] },
      { title: 'MAINTENANCE HISTORY (Last 10)', data: maintenance_history.slice(0, 10), columns: ['Date', 'Type', 'Description', 'Cost (Dh)'] },
      { title: 'FINES HISTORY (Last 10)', data: fines_history.slice(0, 10), columns: ['Date', 'Reason', 'Amount (Dh)', 'Status'] },
      { title: 'SALIK HISTORY (Last 10)', data: salik_history.slice(0, 10), columns: ['Date', 'Gate', 'Amount (Dh)'] },
      { title: 'INSURANCE RENEWALS', data: insurance_history, columns: ['Provider', 'Policy Number', 'Start Date', 'End Date', 'Premium (Dh)'] },
      { title: 'INSURANCE CLAIMS', data: insurance_claims, columns: ['Claim Number', 'Claim Date', 'Type', 'Amount (Dh)', 'Status'] },
      { title: 'ACCIDENTS SUMMARY', data: accidents_history, columns: ['Date', 'Location', 'Severity', 'Description'] },
      { title: 'BATTERY SWAPS (Last 15)', data: battery_swaps_history ? battery_swaps_history.slice(0, 15) : [], columns: ['Date', 'Time', 'Location', 'Return SOC %', 'Issued SOC %'] }
    ];
    
    for (const section of sections) {
      if (currentY > 180) {
        doc.addPage();
        currentY = 20;
      }
      
      doc.setFontSize(9);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.text(section.title, 14, currentY);
      currentY += 5;
      
      if (section.data && section.data.length > 0) {
        const tableData = section.data.map(item => {
          if (section.title.includes('RIDER')) {
            return [item.rider_name || 'N/A', item.start_date || 'N/A', item.end_date || 'Ongoing', item.status || 'N/A', item.duration_days || 'N/A'];
          } else if (section.title.includes('IDLE')) {
            return [item.start_date, item.end_date, item.idle_days];
          } else if (section.title.includes('MAINTENANCE')) {
            return [item.maintenance_date, item.maintenance_type || 'N/A', item.description?.substring(0, 30) || 'N/A', (item.total_cost || 0).toFixed(2)];
          } else if (section.title.includes('FINES')) {
            return [item.fine_date, item.reason?.substring(0, 30) || 'N/A', (item.amount || 0).toFixed(2), item.status || 'N/A'];
          } else if (section.title.includes('SALIK')) {
            return [item.date, item.gate_name || 'N/A', (item.amount || 0).toFixed(2)];
          } else if (section.title.includes('INSURANCE RENEWALS')) {
            return [item.provider, item.policy_number, item.start_date, item.end_date, (item.premium_amount || 0).toFixed(2)];
          } else if (section.title.includes('INSURANCE CLAIMS')) {
            return [item.claim_number, item.claim_date, item.claim_type, (item.claim_amount || 0).toFixed(2), item.status];
          } else if (section.title.includes('ACCIDENTS')) {
            return [item.accident_date, item.location?.substring(0, 25) || 'N/A', item.severity || 'N/A', item.description?.substring(0, 30) || 'N/A'];
          } else if (section.title.includes('BATTERY SWAPS')) {
            const returnSOC = `${item.return_battery_1_soc || 0}/${item.return_battery_2_soc || 0}`;
            const issuedSOC = `${item.issued_battery_1_soc || 0}/${item.issued_battery_2_soc || 0}`;
            return [item.date, item.time, item.location_name?.substring(0, 20) || 'N/A', returnSOC, issuedSOC];
          }
          return [];
        });
        
        autoTable(doc, {
          startY: currentY,
          head: [section.columns],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [139, 195, 74], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          margin: { left: 14, right: 14 }
        });
        
        currentY = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.setFont(undefined, 'normal');
        doc.text('No records found for this section.', 14, currentY);
        currentY += 10;
      }
    }
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.height - 15;
      doc.setDrawColor(139, 195, 74);
      doc.setLineWidth(1.5);
      doc.line(14, footerY, 283, footerY);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, 'normal');
      doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC - Comprehensive Motorcycle History', 14, footerY + 5);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, footerY + 5);
    }
    
    doc.save(`Motorcycle_History_${motorcycle.plate_number.replace(/\s/g, '_')}.pdf`);
  }
};

export const exportRepairItemReport = async (reportData, format = 'excel') => {
  const { repair_item, date_range, summary, records } = reportData;
  
  if (format === 'excel') {
    // Prepare detailed records
    const detailsData = records.map(record => ({
      'Maintenance Date': record.maintenance_date,
      'Bike Plate': record.motorcycle_plate,
      'Bike Model': record.motorcycle_model,
      'Lessee Company': record.lessee_company,
      'Lessee Name': record.lessee_name,
      'Rider Name': record.rider_name,
      'Rider Emirates ID': record.rider_emirates_id,
      'Technician': record.technician_name,
      'Repair Cost (Dh)': record.repair_cost,
      'Total Maintenance Cost (Dh)': record.total_maintenance_cost,
      'Status': record.status,
      'Description': record.description,
      'Notes': record.notes
    }));

    const stats = [
      { label: 'Report Generated', value: getCurrentTimestamp() },
      { label: 'Repair Item', value: repair_item },
      { label: 'Date Range', value: `${date_range.start || 'All'} to ${date_range.end || 'All'}` },
      { label: 'Total Occurrences', value: summary.total_occurrences },
      { label: 'Unique Bikes Affected', value: summary.unique_bikes_affected },
      { label: 'Total Cost', value: `Dh ${summary.total_cost.toLocaleString()}` },
      { label: 'Average Cost', value: `Dh ${summary.average_cost.toLocaleString()}` }
    ];

    await exportToExcel(
      detailsData,
      `Repair_Item_Report_${repair_item.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`,
      `${repair_item} - Repair Analysis`,
      stats
    );
  } else {
    // PDF Export
    const doc = new jsPDF('l', 'mm', 'a4');
    const logoBase64 = await loadLogoAsBase64();
    
    // Add logo and header
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 14, 10, 35, 12);
    }
    
    doc.setFontSize(16);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('REPAIR ITEM ANALYSIS REPORT', doc.internal.pageSize.width / 2, 18, { align: 'center' });
    
    // Report Info
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(`Repair Item: ${repair_item}`, 14, 30);
    doc.text(`Date Range: ${date_range.start || 'All'} to ${date_range.end || 'All'}`, 14, 36);
    doc.text(`Generated: ${getCurrentTimestamp()}`, 14, 42);
    
    // Summary Statistics Box
    doc.setDrawColor(139, 195, 74);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, 48, 268, 28, 2, 2);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(139, 195, 74);
    doc.text('SUMMARY STATISTICS', 18, 55);
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Occurrences: ${summary.total_occurrences}`, 18, 62);
    doc.text(`Unique Bikes Affected: ${summary.unique_bikes_affected}`, 18, 68);
    doc.text(`Total Cost: Dh ${summary.total_cost.toLocaleString()}`, 120, 62);
    doc.text(`Average Cost: Dh ${summary.average_cost.toLocaleString()}`, 120, 68);
    
    // Detailed Records Table
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('DETAILED RECORDS', 14, 85);
    
    const tableData = records.map(record => [
      record.maintenance_date,
      record.motorcycle_plate,
      record.motorcycle_model,
      record.lessee_company,
      record.rider_name,
      `Dh ${record.repair_cost}`,
      record.status
    ]);
    
    autoTable(doc, {
      startY: 88,
      head: [['Date', 'Bike', 'Model', 'Lessee', 'Rider', 'Cost', 'Status']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [139, 195, 74],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 14, right: 14 },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 28 },
        2: { cellWidth: 40 },
        3: { cellWidth: 45 },
        4: { cellWidth: 35 },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 20 }
      }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.height - 15;
      doc.setDrawColor(139, 195, 74);
      doc.setLineWidth(1.5);
      doc.line(14, footerY, doc.internal.pageSize.width - 14, footerY);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, 'normal');
      doc.text('EZY MOBILITY SOLUTIONS - Repair Item Analysis', 14, footerY + 5);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, footerY + 5);
    }
    
    doc.save(`Repair_Item_Report_${repair_item.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  }
};

export const exportBatterySwapsReport = async (reportData, format = 'excel') => {
  const { date_range, summary, swaps } = reportData;
  
  if (format === 'excel') {
    // Prepare detailed records
    const detailsData = swaps.map(swap => ({
      'Date': swap.swap_date || swap.date || 'N/A',
      'Time': swap.swap_time || swap.time || 'N/A',
      'Bike Plate': swap.bike_plate_number || swap.motorcycle_plate || 'N/A',
      'Rider Name': swap.rider_name || 'N/A',
      'Location': swap.location_name || swap.location || 'N/A',
      'ODO Reading': swap.odo_reading || 'N/A',
      'Return Battery 1 Serial': swap.return_battery_1_serial || swap.return_battery1_serial || 'N/A',
      'Return Battery 1 SOC %': swap.return_battery_1_soc ?? swap.return_battery1_soc ?? 'N/A',
      'Return Battery 2 Serial': swap.return_battery_2_serial || swap.return_battery2_serial || 'N/A',
      'Return Battery 2 SOC %': swap.return_battery_2_soc ?? swap.return_battery2_soc ?? 'N/A',
      'Issued Battery 1 Serial': swap.issued_battery_1_serial || swap.issued_battery1_serial || 'N/A',
      'Issued Battery 1 SOC %': swap.issued_battery_1_soc ?? swap.issued_battery1_soc ?? 'N/A',
      'Issued Battery 2 Serial': swap.issued_battery_2_serial || swap.issued_battery2_serial || 'N/A',
      'Issued Battery 2 SOC %': swap.issued_battery_2_soc ?? swap.issued_battery2_soc ?? 'N/A',
      'Notes': swap.notes || ''
    }));

    const stats = [
      { label: 'Report Generated', value: getCurrentTimestamp() },
      { label: 'Date Range', value: `${date_range.start || 'All'} to ${date_range.end || 'All'}` },
      { label: 'Total Swaps', value: summary.total_swaps },
      { label: 'Unique Bikes', value: summary.unique_bikes },
      { label: 'Unique Riders', value: summary.unique_riders },
      { label: 'Unique Locations', value: summary.unique_locations },
      { label: 'Avg Return SOC', value: `${summary.average_return_soc}%` },
      { label: 'Avg Issued SOC', value: `${summary.average_issued_soc}%` }
    ];

    await exportToExcel(
      detailsData,
      `Battery_Swaps_Report_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Battery Swaps Report',
      stats
    );
  } else {
    // PDF Export
    const doc = new jsPDF('l', 'mm', 'a4');
    const logoBase64 = await loadLogoAsBase64();
    
    // Add logo and header
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 14, 10, 35, 12);
    }
    
    doc.setFontSize(16);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('BATTERY SWAPS REPORT', doc.internal.pageSize.width / 2, 18, { align: 'center' });
    
    // Report Info
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text(`Date Range: ${date_range.start || 'All'} to ${date_range.end || 'All'}`, 14, 30);
    doc.text(`Generated: ${getCurrentTimestamp()}`, 14, 36);
    
    // Summary Statistics Box
    doc.setDrawColor(139, 195, 74);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, 42, 268, 28, 2, 2);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(139, 195, 74);
    doc.text('SUMMARY STATISTICS', 18, 49);
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Swaps: ${summary.total_swaps}`, 18, 56);
    doc.text(`Unique Bikes: ${summary.unique_bikes}`, 18, 62);
    doc.text(`Unique Riders: ${summary.unique_riders}`, 90, 56);
    doc.text(`Unique Locations: ${summary.unique_locations}`, 90, 62);
    doc.text(`Avg Return SOC: ${summary.average_return_soc}%`, 165, 56);
    doc.text(`Avg Issued SOC: ${summary.average_issued_soc}%`, 165, 62);
    
    // Detailed Records Table
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('DETAILED RECORDS', 14, 79);
    
    const tableData = swaps.map(swap => [
      swap.swap_date || swap.date || 'N/A',
      swap.swap_time || swap.time || 'N/A',
      swap.bike_plate_number || swap.motorcycle_plate || 'N/A',
      swap.rider_name || 'N/A',
      swap.location_name || swap.location || 'N/A',
      swap.odo_reading || 'N/A',
      `${swap.return_battery_1_serial || swap.return_battery1_serial || 'N/A'}\n${swap.return_battery_1_soc ?? swap.return_battery1_soc ?? 'N/A'}%`,
      `${swap.issued_battery_1_serial || swap.issued_battery1_serial || 'N/A'}\n${swap.issued_battery_1_soc ?? swap.issued_battery1_soc ?? 'N/A'}%`
    ]);
    
    autoTable(doc, {
      startY: 82,
      head: [['Date', 'Time', 'Bike', 'Rider', 'Location', 'ODO', 'Return Bat1\n(Serial/SOC)', 'Issued Bat1\n(Serial/SOC)']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [139, 195, 74],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7
      },
      bodyStyles: {
        fontSize: 6,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 14, right: 14 },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 15 },
        2: { cellWidth: 25 },
        3: { cellWidth: 40 },
        4: { cellWidth: 40 },
        5: { cellWidth: 18 },
        6: { cellWidth: 35 },
        7: { cellWidth: 35 }
      }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.height - 15;
      doc.setDrawColor(139, 195, 74);
      doc.setLineWidth(1.5);
      doc.line(14, footerY, doc.internal.pageSize.width - 14, footerY);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, 'normal');
      doc.text('EZY MOBILITY SOLUTIONS - Battery Swaps Report', 14, footerY + 5);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, footerY + 5);
    }
    
    doc.save(`Battery_Swaps_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  }
};

// Export Battery History Report (for individual battery swap history)
export const exportBatteryHistoryReport = async (batterySerial, historyData, format = 'excel') => {
  // Calculate summary statistics
  const totalSwaps = historyData.length;
  const returnedCount = historyData.filter(h => h.battery_type === 'returned').length;
  const issuedCount = historyData.filter(h => h.battery_type === 'issued').length;
  const uniqueRiders = [...new Set(historyData.filter(h => h.rider_name).map(h => h.rider_name))].length;
  const uniqueBikes = [...new Set(historyData.filter(h => h.bike_number).map(h => h.bike_number))].length;
  const uniqueLocations = [...new Set(historyData.filter(h => h.location).map(h => h.location))].length;
  
  // Calculate SOC averages
  const socValues = historyData.filter(h => h.soc !== null && h.soc !== undefined).map(h => parseFloat(h.soc));
  const avgSoc = socValues.length > 0 ? (socValues.reduce((a, b) => a + b, 0) / socValues.length).toFixed(1) : 'N/A';
  
  // Get date range from history
  const dates = historyData.filter(h => h.swap_date).map(h => h.swap_date);
  const dateRange = dates.length > 0 
    ? { start: dates[dates.length - 1].split('T')[0], end: dates[0].split('T')[0] }
    : { start: 'N/A', end: 'N/A' };

  // Helper function to safely format datetime
  const formatDateTime = (record) => {
    // First try the raw date and time fields
    if (record.date) {
      const time = record.time || '00:00';
      return `${formatDateDisplay(record.date)} ${time}`;
    }
    // Fallback to swap_date
    if (record.swap_date) {
      try {
        const d = new Date(record.swap_date);
        if (!isNaN(d.getTime())) {
          return formatDateTimeDisplay(d);
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return 'N/A';
  };

  if (format === 'excel') {
    // Prepare detailed records for Excel
    const detailsData = historyData.map(record => ({
      'Date & Time': formatDateTime(record),
      'Action': record.battery_type === 'returned' ? 'Returned' : 'Issued',
      'Position': record.battery_position || 'N/A',
      'SOC %': record.soc !== null && record.soc !== undefined ? `${record.soc}%` : 'N/A',
      'Rider Name': record.rider_name || 'N/A',
      'Rider ID': record.rider_unique_id || 'N/A',
      'Bike Plate': record.bike_number || 'N/A',
      'Lessee Company': record.lessee_company || 'N/A',
      'ODO Reading': record.odo_reading || 'N/A',
      'Location': record.location || 'N/A',
      'Technician': record.technician_name || 'N/A'
    }));

    const stats = [
      { label: 'Battery Serial', value: batterySerial },
      { label: 'Report Generated', value: getCurrentTimestamp() },
      { label: 'Date Range', value: `${formatDateDisplay(dateRange.start)} to ${formatDateDisplay(dateRange.end)}` },
      { label: 'Total Swap Records', value: totalSwaps },
      { label: 'Times Returned', value: returnedCount },
      { label: 'Times Issued', value: issuedCount },
      { label: 'Unique Riders', value: uniqueRiders },
      { label: 'Unique Bikes', value: uniqueBikes },
      { label: 'Unique Locations', value: uniqueLocations },
      { label: 'Average SOC', value: avgSoc !== 'N/A' ? `${avgSoc}%` : avgSoc }
    ];

    await exportToExcel(
      detailsData,
      `Battery_History_${batterySerial.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Battery History Report',
      stats
    );
  } else {
    // PDF Export
    const doc = new jsPDF('l', 'mm', 'a4');
    const logoBase64 = await loadLogoAsBase64();
    
    // Add logo and header
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 14, 10, 35, 12);
    }
    
    doc.setFontSize(16);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('BATTERY HISTORY REPORT', doc.internal.pageSize.width / 2, 18, { align: 'center' });
    
    // Battery Serial prominent display
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'bold');
    doc.text(`Battery: ${batterySerial}`, doc.internal.pageSize.width / 2, 26, { align: 'center' });
    
    // Report Info
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Date Range: ${formatDateDisplay(dateRange.start)} to ${formatDateDisplay(dateRange.end)}`, 14, 34);
    doc.text(`Generated: ${getCurrentTimestamp()}`, 14, 40);
    
    // Summary Statistics Box
    doc.setDrawColor(139, 195, 74);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, 46, 268, 30, 2, 2);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(139, 195, 74);
    doc.text('SUMMARY STATISTICS', 18, 53);
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    
    // Row 1
    doc.text(`Total Records: ${totalSwaps}`, 18, 60);
    doc.text(`Times Returned: ${returnedCount}`, 75, 60);
    doc.text(`Times Issued: ${issuedCount}`, 130, 60);
    doc.text(`Average SOC: ${avgSoc !== 'N/A' ? avgSoc + '%' : avgSoc}`, 185, 60);
    
    // Row 2
    doc.text(`Unique Riders: ${uniqueRiders}`, 18, 68);
    doc.text(`Unique Bikes: ${uniqueBikes}`, 75, 68);
    doc.text(`Unique Locations: ${uniqueLocations}`, 130, 68);
    
    // Detailed Records Table
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('SWAP HISTORY', 14, 85);
    
    // Helper function to format date safely
    const formatDate = (record) => {
      // First try the raw date field
      if (record.date) {
        return formatDateDisplay(record.date);
      }
      // Fallback to swap_date if available
      if (record.swap_date) {
        try {
          const d = new Date(record.swap_date);
          if (!isNaN(d.getTime())) {
            return formatDateDisplay(d);
          }
        } catch (e) { /* ignore parse errors */ }
      }
      return 'N/A';
    };
    
    // Helper function to format time safely
    const formatTime = (record) => {
      // First try the raw time field
      if (record.time) {
        return record.time;
      }
      // Fallback to swap_date if available
      if (record.swap_date) {
        try {
          const d = new Date(record.swap_date);
          if (!isNaN(d.getTime())) {
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
          }
        } catch (e) { /* ignore parse errors */ }
      }
      return 'N/A';
    };
    
    const tableData = historyData.map(record => [
      formatDate(record),
      formatTime(record),
      record.battery_type === 'returned' ? 'Returned' : 'Issued',
      record.soc !== null && record.soc !== undefined ? `${record.soc}%` : 'N/A',
      record.rider_name || 'N/A',
      record.bike_number || 'N/A',
      record.lessee_company || 'N/A',
      record.location || 'N/A',
      record.technician_name || 'N/A'
    ]);
    
    autoTable(doc, {
      startY: 88,
      head: [['Date', 'Time', 'Action', 'SOC', 'Rider', 'Bike', 'Lessee', 'Location', 'Technician']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [139, 195, 74],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7
      },
      bodyStyles: {
        fontSize: 6,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 14, right: 14 },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 15 },
        2: { cellWidth: 18 },
        3: { cellWidth: 15 },
        4: { cellWidth: 35 },
        5: { cellWidth: 30 },
        6: { cellWidth: 40 },
        7: { cellWidth: 35 },
        8: { cellWidth: 30 }
      }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.height - 15;
      doc.setDrawColor(139, 195, 74);
      doc.setLineWidth(1.5);
      doc.line(14, footerY, doc.internal.pageSize.width - 14, footerY);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, 'normal');
      doc.text(`EZY MOBILITY SOLUTIONS - Battery History Report (${batterySerial})`, 14, footerY + 5);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, footerY + 5);
    }
    
    doc.save(`Battery_History_${batterySerial.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  }
};